import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authAPI } from '../lib/api';

// Mock data for fallback
const mockUsers = {
  'admin@accountingpro.com': { 
    password: 'admin123', 
    role: 'admin', 
    fullName: 'Admin User',
    companyId: 'mock-company-1',
    company: {
      id: 'mock-company-1',
      name: 'AccountingPro Demo',
      address: '123 Business Avenue, Suite 100, New York, NY 10001, United States',
      phone: '(555) 123-4567',
      email: 'contact@accountingpro.com',
      taxId: 'TAX123456789',
      currency: 'USD',
      fiscalYearStart: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  },
  'user@accountingpro.com': { 
    password: 'user123', 
    role: 'user', 
    fullName: 'Regular User',
    companyId: 'mock-company-1',
    company: {
      id: 'mock-company-1',
      name: 'AccountingPro Demo',
      address: '123 Business Avenue, Suite 100, New York, NY 10001, United States',
      phone: '(555) 123-4567',
      email: 'contact@accountingpro.com',
      taxId: 'TAX123456789',
      currency: 'USD',
      fiscalYearStart: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  },
  'accountant@accountingpro.com': { 
    password: 'accountant123', 
    role: 'accountant', 
    fullName: 'Senior Accountant',
    companyId: 'mock-company-1',
    company: {
      id: 'mock-company-1',
      name: 'AccountingPro Demo',
      address: '123 Business Avenue, Suite 100, New York, NY 10001, United States',
      phone: '(555) 123-4567',
      email: 'contact@accountingpro.com',
      taxId: 'TAX123456789',
      currency: 'USD',
      fiscalYearStart: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  }
};

export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials) => {
      try {
        console.log('Attempting login with:', credentials.email);
        const response = await authAPI.login(credentials);
        
        // Handle Crato API response format
        if (response.data && response.data.Success) {
          const { Token, User, Company, ExpiresAt } = response.data.Data;
          
          // Store the token and user data
          localStorage.setItem('auth_token', Token);
          localStorage.setItem('token_expires_at', ExpiresAt);
          
          const userData = {
            id: User.Id,
            email: User.Email,
            fullName: User.FullName,
            role: User.Role,
            companyId: User.CompanyId,
            avatarUrl: User.AvatarUrl,
            createdAt: User.CreatedAt,
            updatedAt: User.UpdatedAt,
            // Include company data in user object
            company: {
              id: Company.Id,
              name: Company.Name,
              address: Company.Address,
              phone: Company.Phone,
              email: Company.Email,
              taxId: Company.TaxId,
              currency: Company.Currency,
              fiscalYearStart: Company.FiscalYearStart,
              createdAt: Company.CreatedAt,
              updatedAt: Company.UpdatedAt
            },
            currency: Company.Currency
          };
          
          localStorage.setItem('current_user', JSON.stringify(userData));
          
          console.log('Login successful:', User.FullName, 'Company:', Company.Name);
          
          return { success: true, user: userData };
        } else {
          throw new Error(response.data?.Message || 'Login failed');
        }
      } catch (error) {
        console.warn('API login failed, using mock authentication:', error.message);
        
        // Fallback to mock authentication
        const { email, password } = credentials;
        const user = mockUsers[email];
        
        if (user && user.password === password) {
          // Store mock token for consistency
          const mockToken = 'mock-token-' + Date.now();
          const mockExpiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
          
          localStorage.setItem('auth_token', mockToken);
          localStorage.setItem('token_expires_at', mockExpiresAt);
          
          const userData = {
            id: email,
            email,
            fullName: user.fullName,
            role: user.role,
            companyId: user.companyId,
            avatarUrl: null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            company: user.company,
            currency: user.company.currency
          };
          
          localStorage.setItem('current_user', JSON.stringify(userData));
          
          return { success: true, user: userData };
        }
        
        throw new Error('Invalid credentials');
      }
    },
    onSuccess: () => {
      // Invalidate and refetch user-related queries
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
    onError: (error) => {
      console.error('Login mutation error:', error);
    }
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Clear local storage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('current_user');
      localStorage.removeItem('token_expires_at');
      
      return { success: true };
    },
    onSuccess: () => {
      // Clear all cached data
      queryClient.clear();
    }
  });
};